import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Clock } from "lucide-react";
import { Subscription } from "@/types/subscription";

interface SubscriptionPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  onSuccess: () => void;
}

/**
 * Renouvellement d'abonnement.
 *
 * Le paiement en ligne n'est pas encore branché : aucun prestataire n'a été
 * retenu. Cet écran affichait auparavant un formulaire de carte bancaire dont
 * le traitement était simulé (`setTimeout` + `Math.random() < 0.9`) : il
 * annonçait « Paiement réussi » sans rien débiter ni renouveler, et
 * collectait de vraies coordonnées bancaires au passage. Il est remplacé par
 * une information claire tant que l'intégration n'est pas faite.
 */
export function SubscriptionPaymentDialog({
  open,
  onOpenChange,
  subscription,
}: SubscriptionPaymentDialogProps) {
  const { t } = useTranslation(["subscriptions", "common"]);
  const { formatCurrency } = useCurrency();

  const price = subscription?.price;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t("renewSubscription", { ns: "subscriptions", defaultValue: "Renouveler l'abonnement" })}
          </DialogTitle>
          {price ? (
            <DialogDescription>
              {t(`plans.${subscription?.type?.toLowerCase()}.title`, {
                ns: "subscriptions",
                defaultValue: subscription?.type,
              })}{" "}
              - {formatCurrency(price)}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="font-medium">
              {t("onlinePaymentComingSoon", {
                ns: "subscriptions",
                defaultValue: "Le paiement en ligne arrive prochainement",
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("onlinePaymentComingSoonHint", {
                ns: "subscriptions",
                defaultValue:
                  "En attendant, rapprochez-vous de l'accueil de votre salle pour renouveler votre abonnement.",
              })}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t("close", { ns: "common", defaultValue: "Fermer" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
